import React from "react";
import { cn } from "lib/utils";

export type SetupStepState = "complete" | "active" | "failed" | "upcoming";

export interface SetupStep {
  label: string;
  state: SetupStepState;
}

export interface EnvironmentSetupLightsProps {
  steps: readonly SetupStep[];
  disabled?: boolean;
  className?: string;
}

export const setupLightClassNames: Record<SetupStepState, string> = {
  active:
    "animate-pulse bg-highlight shadow-[0_0_8px_rgba(118,185,0,0.55)] motion-reduce:animate-none",
  complete: "bg-highlight shadow-[0_0_8px_rgba(118,185,0,0.55)]",
  failed: "bg-red-500 shadow-[0_0_8px_rgba(239,68,68,0.55)]",
  upcoming: "bg-zinc-800 ring-1 ring-zinc-700/70 shadow-inner",
};

type SetupLightStage = "provisioning" | "building" | "startup" | "ready";

const setupLightStageClassNames: Record<
  SetupLightStage,
  { active: string; upcoming: string }
> = {
  building: {
    active:
      "animate-pulse bg-pendingBlue shadow-[0_0_8px] shadow-pendingBlue/45 motion-reduce:animate-none",
    upcoming:
      "bg-pendingBlue/20 ring-1 ring-pendingBlue/45 shadow-[0_0_8px] shadow-pendingBlue/30",
  },
  provisioning: {
    active:
      "animate-pulse bg-pendingBlue shadow-[0_0_8px] shadow-pendingBlue/45 motion-reduce:animate-none",
    upcoming:
      "bg-pendingBlue/20 ring-1 ring-pendingBlue/45 shadow-[0_0_8px] shadow-pendingBlue/30",
  },
  ready: {
    active:
      "animate-pulse bg-highlight shadow-[0_0_8px_rgba(118,185,0,0.55)] motion-reduce:animate-none",
    upcoming:
      "bg-highlight/20 ring-1 ring-highlight/45 shadow-[0_0_8px_rgba(118,185,0,0.3)]",
  },
  startup: {
    active:
      "animate-pulse bg-highlight shadow-[0_0_8px_rgba(118,185,0,0.55)] motion-reduce:animate-none",
    upcoming:
      "bg-highlight/20 ring-1 ring-highlight/45 shadow-[0_0_8px_rgba(118,185,0,0.3)]",
  },
};

const getSetupLightStage = (label: string): SetupLightStage => {
  const normalizedLabel = label.toLowerCase();
  if (normalizedLabel.includes("provision")) return "provisioning";
  if (normalizedLabel.includes("build")) return "building";
  if (normalizedLabel.includes("startup")) return "startup";
  return "ready";
};

const getCurrentSetupStep = (steps: readonly SetupStep[]): SetupStep =>
  steps.find((step) => step.state === "failed") ??
  steps.find((step) => step.state === "active") ??
  (steps.every((step) => step.state === "complete")
    ? steps[steps.length - 1]
    : steps.find((step) => step.state === "upcoming")) ??
  steps[steps.length - 1];

const getSetupLightClassName = (step: SetupStep): string => {
  if (step.state === "complete") return setupLightClassNames.complete;
  if (step.state === "failed") return setupLightClassNames.failed;

  const stageClasses =
    setupLightStageClassNames[getSetupLightStage(step.label)];
  return stageClasses[step.state];
};

export const EnvironmentSetupLights: React.FC<EnvironmentSetupLightsProps> = ({
  steps,
  disabled = false,
  className,
}) => {
  if (steps.length === 0) return null;

  const currentStep = getCurrentSetupStep(steps);

  return (
    <span
      className={cn(
        "inline-flex size-[0.55rem] shrink-0 items-center",
        className,
      )}
      role="img"
      aria-label={steps
        .map((step) => `${step.label}: ${disabled ? "disabled" : step.state}`)
        .join(", ")}
    >
      <span
        className={cn(
          "size-[0.55rem] rounded-full",
          disabled
            ? "bg-zinc-700 shadow-none"
            : getSetupLightClassName(currentStep),
        )}
        data-setup-state={currentStep.state}
        data-setup-stage={getSetupLightStage(currentStep.label)}
        data-disabled={disabled || undefined}
        title={`${currentStep.label}: ${disabled ? "disabled" : currentStep.state}`}
        aria-hidden="true"
      />
    </span>
  );
};
